/**
 * useTextAreaDrag — drag/resize state machine for text area positioning.
 *
 * Converts screen-space mouse movements to design-coordinate text_box updates.
 * All drag is client-side CSS; onCommit fires on mouseup with final coordinates.
 */
import { useState, useCallback, useRef } from 'react'
import type { TextBox, ViewPort } from '@/lib/api/contentPacks'

export type DragMode =
  | 'move'
  | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se'
  | 'resize-n' | 'resize-s' | 'resize-e' | 'resize-w'

interface DragStart {
  mouseX: number
  mouseY: number
  box: TextBox
}

const MIN_SIZE = 50

function clampBox(box: TextBox, vp: ViewPort): TextBox {
  let { top, left, width, height } = box
  width = Math.max(MIN_SIZE, Math.min(width, vp.width))
  height = Math.max(MIN_SIZE, Math.min(height, vp.height))
  left = Math.max(0, Math.min(left, vp.width - width))
  top = Math.max(0, Math.min(top, vp.height - height))
  return { top: Math.round(top), left: Math.round(left), width: Math.round(width), height: Math.round(height) }
}

export function useTextAreaDrag({
  viewPort,
  textBox,
  mirrorMode,
  onUpdate,
  onCommit,
}: {
  viewPort: ViewPort
  textBox: TextBox
  mirrorMode: boolean
  onUpdate: (box: TextBox) => void
  onCommit: (box: TextBox) => void
}) {
  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef<{ mode: DragMode; start: DragStart } | null>(null)
  const latestBoxRef = useRef<TextBox>(textBox)

  const toDelta = useCallback((dx: number, dy: number, imageRect: DOMRect) => ({
    dx: imageRect.width > 0 ? (dx / imageRect.width) * viewPort.width : 0,
    dy: imageRect.height > 0 ? (dy / imageRect.height) * viewPort.height : 0,
  }), [viewPort])

  const handlePointerDown = useCallback((
    e: React.PointerEvent,
    mode: DragMode,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    dragRef.current = {
      mode,
      start: { mouseX: e.clientX, mouseY: e.clientY, box: { ...textBox } },
    }
    setIsDragging(true)
  }, [textBox])

  const handlePointerMove = useCallback((
    e: React.PointerEvent,
    imageRect: DOMRect,
  ) => {
    const drag = dragRef.current
    if (!drag) return

    const { dx, dy } = toDelta(
      e.clientX - drag.start.mouseX,
      e.clientY - drag.start.mouseY,
      imageRect,
    )
    const orig = drag.start.box
    let newBox: TextBox

    switch (drag.mode) {
      case 'move':
        if (mirrorMode) {
          newBox = { top: orig.top + dy, left: orig.left, width: orig.width, height: orig.height }
        } else {
          newBox = { top: orig.top + dy, left: orig.left + dx, width: orig.width, height: orig.height }
        }
        break
      case 'resize-se':
        newBox = { top: orig.top, left: orig.left, width: orig.width + dx, height: orig.height + dy }
        break
      case 'resize-sw':
        newBox = { top: orig.top, left: orig.left + dx, width: orig.width - dx, height: orig.height + dy }
        break
      case 'resize-ne':
        newBox = { top: orig.top + dy, left: orig.left, width: orig.width + dx, height: orig.height - dy }
        break
      case 'resize-nw':
        newBox = { top: orig.top + dy, left: orig.left + dx, width: orig.width - dx, height: orig.height - dy }
        break
      case 'resize-n':
        newBox = { top: orig.top + dy, left: orig.left, width: orig.width, height: orig.height - dy }
        break
      case 'resize-s':
        newBox = { top: orig.top, left: orig.left, width: orig.width, height: orig.height + dy }
        break
      case 'resize-e':
        if (mirrorMode) {
          newBox = { top: orig.top, left: orig.left - dx, width: orig.width + dx * 2, height: orig.height }
        } else {
          newBox = { top: orig.top, left: orig.left, width: orig.width + dx, height: orig.height }
        }
        break
      case 'resize-w':
        if (mirrorMode) {
          newBox = { top: orig.top, left: orig.left + dx, width: orig.width - dx * 2, height: orig.height }
        } else {
          newBox = { top: orig.top, left: orig.left + dx, width: orig.width - dx, height: orig.height }
        }
        break
      default:
        return
    }

    const clamped = clampBox(newBox, viewPort)
    latestBoxRef.current = clamped
    onUpdate(clamped)
  }, [toDelta, mirrorMode, viewPort, onUpdate])

  const handlePointerUp = useCallback(() => {
    if (!dragRef.current) return
    dragRef.current = null
    setIsDragging(false)
    onCommit(latestBoxRef.current)
  }, [onCommit])

  return {
    isDragging,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  }
}

export function alignTextBox(
  action: 'h-center' | 'v-center' | 'top-third' | 'bot-third' | 'full-width',
  box: TextBox,
  vp: ViewPort,
): TextBox {
  switch (action) {
    case 'h-center':
      return { ...box, left: Math.round((vp.width - box.width) / 2) }
    case 'v-center':
      return { ...box, top: Math.round((vp.height - box.height) / 2) }
    case 'top-third':
      return { ...box, top: Math.round((vp.height - box.height) / 3) }
    case 'bot-third':
      return { ...box, top: Math.round((vp.height - box.height) * 2 / 3) }
    case 'full-width':
      return { ...box, left: 0, width: vp.width }
    default:
      return box
  }
}
